import { Container } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeDisplay } from './QRCodeDisplay';
import styled from '@emotion/styled';

// Styled Components
const UploadContainer = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 24px;
  width: 100%;
  max-width: 600px;
  margin: 20px auto;
`;

const UploadHeader = styled.div`
  margin-bottom: 16px;
`;

const UploadTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
`;

const EncounterId = styled.div`
  font-size: 14px;
  color: #666;
  margin-top: 4px;
`;

const Instructions = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
  line-height: 1.5;
`;

const FileUpload = styled.div`
  position: relative;
  padding: 40px;
  border-radius: 8px;
  background-color: #fafafa;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-bottom: 24px;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const FileUploadGrid = styled.div`
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
      0deg,
      #e0e0e0 0px 1px,
      transparent 1px 20px
    ),
    repeating-linear-gradient(
      90deg,
      #e0e0e0 0px 1px,
      transparent 1px 20px
    );
  opacity: 0.3;
  pointer-events: none;
`;

const FileUploadContent = styled.div`
  position: relative;
  z-index: 1;
`;

const FileUploadIcon = styled.div`
  width: 48px;
  height: 48px;
  background-color: #1976d2;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 16px;
  color: #ffffff;
  font-size: 24px;
`;

const FileUploadTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const FileUploadSubtitle = styled.div`
  font-size: 14px;
  color: #666;
`;

const FileInput = styled.input`
  display: none;
`;

const SubmitButton = styled.button`
  display: block;
  width: 100%;
  padding: 12px;
  background-color: #1976d2;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #1565c0;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const QRCodeSection = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

export function CaptureUploadPage(): JSX.Element {
  const { patientId } = useParams();
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const uploadToSignedUrl = async (signedUrl: string, file: File) => {
    try {
      console.log('Starting upload to:', signedUrl);

      const response = await fetch(signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        body: file,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const createCaptureSession = async () => {
    if (!selectedFile) {
      notifications.show({
        title: 'Error',
        message: 'Please select a file to upload',
        color: 'red',
      });
      return;
    }

    try {
      setLoading(true);
      console.log('Creating capture session...');

      const requestBody = {
        title: `Capture Session for Patient ${patientId}`,
        patientId,
      };

      const token = medplum.getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      console.log('Token payload:', JSON.parse(atob(token.split('.')[1])));
      
      const response = await fetch('/api/v1/capture', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);

      if (!response.ok) {
        throw new Error(`API error: ${response.status} - ${JSON.stringify(data)}`);
      }

      if (data.guid && data.signedUrls?.source) {
        await uploadToSignedUrl(data.signedUrls.source, selectedFile);

        notifications.show({
          title: 'Success',
          message: 'File uploaded successfully',
          color: 'green',
        });

        navigate(`/Patient/${patientId}/capture/${data.guid}`);
      } else {
        throw new Error('No session GUID or signed URL in response');
      }
    } catch (error) {
      console.error('Capture session error:', error);
      let errorMessage = 'Failed to create capture session';
      if (error instanceof Error) {
        errorMessage += `: ${error.message}`;
      }
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  return (
    <Container size="sm" p="xl">
      <UploadContainer>
        <UploadHeader>
          <UploadTitle>Upload Wound Footage</UploadTitle>
          <EncounterId>Patient ID: {patientId}</EncounterId>
        </UploadHeader>

        <Instructions>
          Scan the QR code with your phone to link the session, or upload wound footage here. 
          Ensure the footage includes clear views of the wound bed, wound edge, periwound, and normal skin.
        </Instructions>

        <QRCodeSection>
          <QRCodeDisplay patientId={patientId} />
        </QRCodeSection>

        <FileUpload
          onClick={() => document.getElementById('file-upload')?.click()}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <FileUploadGrid />
          <FileUploadContent>
            <FileUploadIcon>↑</FileUploadIcon>
            <FileUploadTitle>Upload Footage</FileUploadTitle>
            <FileUploadSubtitle>
              {selectedFile 
                ? `Selected: ${selectedFile.name}`
                : 'Drag and drop your files here or click to upload'}
            </FileUploadSubtitle>
            <FileInput
              type="file"
              id="file-upload"
              accept=".zip,application/zip,application/octet-stream,video/mp4"
              onChange={handleFileChange}
            />
          </FileUploadContent>
        </FileUpload>

        <SubmitButton
          onClick={createCaptureSession}
          disabled={!selectedFile || loading}
        >
          {loading ? 'Uploading...' : 'Submit and Proceed'}
        </SubmitButton>
      </UploadContainer>
    </Container>
  );
}